import { useEffect, useRef, memo } from "react";
import * as d3 from "d3";
import { sankey, sankeyLinkHorizontal, type SankeyGraph } from "d3-sankey";

export interface SankeyNode {
  name: string;
  color?: string;
}

export interface SankeyLink {
  source: number;
  target: number;
  value: number;
}

export interface SankeyData {
  nodes: SankeyNode[];
  links: SankeyLink[];
}

interface D3SankeyChartProps {
  data: SankeyData;
  width?: number;
  height?: number;
}

const ACCENT_PALETTE = [
  "#60a5fa",
  "#a78bfa",
  "#22d3ee",
  "#10b981",
  "#f59e0b",
  "#fb7185",
  "#e879f9",
];

export const D3SankeyChart = memo(
  ({ data, width = 900, height = 500 }: D3SankeyChartProps) => {
    const svgRef = useRef<SVGSVGElement>(null);

    useEffect(() => {
      if (!svgRef.current || !data.nodes.length) return;

      const svg = d3.select(svgRef.current);
      svg.selectAll("*").remove();

      const margin = { top: 12, right: 12, bottom: 12, left: 12 };
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;

      const g = svg
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      const sankeyLayout = sankey<SankeyNode, SankeyLink>()
        .nodeWidth(16)
        .nodePadding(14)
        .extent([
          [1, 1],
          [innerWidth - 1, innerHeight - 5],
        ]);

      // d3-sankey mutates the input; clone to avoid surprises.
      const cloned: SankeyData = {
        nodes: data.nodes.map((n) => ({ ...n })),
        links: data.links.map((l) => ({ ...l })),
      };
      const graph = sankeyLayout(cloned as unknown as SankeyGraph<SankeyNode, SankeyLink>);

      const fallback = d3.scaleOrdinal<string, string>().range(ACCENT_PALETTE);
      const colorOf = (n: any) => (n.color as string | undefined) ?? fallback(n.name);

      // Links
      g.append("g")
        .attr("fill", "none")
        .selectAll("path")
        .data(graph.links)
        .enter()
        .append("path")
        .attr("d", sankeyLinkHorizontal() as any)
        .attr("stroke", (d: any) => colorOf(d.source))
        .attr("stroke-width", (d: any) => Math.max(1, d.width))
        .attr("opacity", 0.45)
        .on("mouseover", function () {
          d3.select(this).attr("opacity", 0.8);
        })
        .on("mouseout", function () {
          d3.select(this).attr("opacity", 0.45);
        })
        .append("title")
        .text((d: any) => `${d.source.name} → ${d.target.name}\n${d.value.toLocaleString()}`);

      // Nodes
      const node = g
        .append("g")
        .selectAll("g")
        .data(graph.nodes)
        .enter()
        .append("g");

      node
        .append("rect")
        .attr("x", (d: any) => d.x0)
        .attr("y", (d: any) => d.y0)
        .attr("height", (d: any) => Math.max(1, d.y1 - d.y0))
        .attr("width", sankeyLayout.nodeWidth())
        .attr("fill", (d: any) => colorOf(d))
        .attr("stroke", "rgba(3,6,13,0.9)")
        .attr("stroke-width", 1.5)
        .style("filter", "drop-shadow(0 0 4px rgba(96,165,250,0.35))")
        .append("title")
        .text((d: any) => `${d.name}\n${d.value?.toLocaleString?.() ?? ""}`);

      node
        .append("text")
        .attr("x", (d: any) => (d.x0 < innerWidth / 2 ? d.x1 + 8 : d.x0 - 8))
        .attr("y", (d: any) => (d.y1 + d.y0) / 2)
        .attr("dy", "0.35em")
        .attr("text-anchor", (d: any) => (d.x0 < innerWidth / 2 ? "start" : "end"))
        .text((d: any) => d.name)
        .style("font-size", "12px")
        .style("font-family", "ui-monospace, SFMono-Regular, Menlo, monospace")
        .style("fill", "rgba(226,232,240,0.85)")
        .style("font-weight", "500");
    }, [data, width, height]);

    return (
      <svg
        ref={svgRef}
        width={width}
        height={height}
        className="overflow-visible"
        style={{ maxWidth: "100%" }}
      />
    );
  },
);

D3SankeyChart.displayName = "D3SankeyChart";
